import React, { useEffect, useState, useCallback } from "react";
import { TrendingUp, TrendingDown, Users, Receipt, CalendarCheck, BadgeCheck } from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, BarChart, Bar, Cell,
} from "recharts";
import { api } from "@/lib/api";
import { formatIDR, formatShort, monthStartISO, todayISO } from "@/lib/format";
import { PageHeader, Card, Loading, Pill } from "@/components/kit";

const PRESETS = [
  { label: "Hari ini", from: todayISO(), to: todayISO() },
  { label: "Bulan ini", from: monthStartISO(), to: todayISO() },
  { label: "30 Hari", from: new Date(Date.now() - 29 * 864e5).toISOString().slice(0, 10), to: todayISO() },
];

function Delta({ v }) {
  if (v == null) return null;
  const up = v >= 0;
  const Icon = up ? TrendingUp : TrendingDown;
  return (
    <span className={`inline-flex items-center gap-1 text-xs ${up ? "text-[#7CD992]" : "text-[#FF8080]"}`}>
      <Icon className="h-3 w-3" /> {Math.abs(v)}%
    </span>
  );
}

export default function Dashboard() {
  const [range, setRange] = useState({ from: monthStartISO(), to: todayISO() });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await api.ownerAnalytics(range.from, range.to)); }
    catch { setData(null); }
    finally { setLoading(false); }
  }, [range]);

  useEffect(() => { load(); }, [load]);

  const ex = data?.executive || {};
  const trend = (data?.revenue_trend?.axis || []).map((a, i) => ({ x: a, v: data.revenue_trend.values[i] }));
  const byLine = data?.revenue_by_line || [];
  const LINE_COLORS = ["#D1D1D6", "#8FD3E8", "#E7C25B", "#C08457", "#7CD992", "#A1A1A6"];

  return (
    <div>
      <PageHeader eyebrow="Owner" title="Business Intelligence"
        subtitle="Ringkasan performa klub berdasarkan data transaksi nyata."
        right={
          <div className="flex flex-wrap gap-2">
            {PRESETS.map((p) => (
              <Pill key={p.label} active={range.from === p.from && range.to === p.to} onClick={() => setRange({ from: p.from, to: p.to })}>
                {p.label}
              </Pill>
            ))}
          </div>
        }
      />
      <div className="mb-6 flex flex-wrap items-center gap-3">
        <input type="date" className="hsc-input w-auto" value={range.from} max={range.to} onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))} data-testid="dash-from" />
        <span className="text-[#6E6E73]">—</span>
        <input type="date" className="hsc-input w-auto" value={range.to} min={range.from} max={todayISO()} onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))} data-testid="dash-to" />
      </div>

      {loading ? <Loading /> : !data ? (
        <Card>Gagal memuat analitik.</Card>
      ) : (
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
            <Card className="fade-in" data-testid="kpi-revenue">
              <div className="flex items-center justify-between"><div className="hsc-label">Revenue</div><Receipt className="h-4 w-4 text-[#3A3A3C]" /></div>
              <div className="mt-2 font-display text-2xl text-white">{formatIDR(ex.revenue)}</div>
              <div className="mt-1"><Delta v={ex.revenue_change} /></div>
            </Card>
            <Card data-testid="kpi-transactions">
              <div className="flex items-center justify-between"><div className="hsc-label">Transaksi</div><Receipt className="h-4 w-4 text-[#3A3A3C]" /></div>
              <div className="mt-2 font-display text-2xl text-white">{ex.transactions || 0}</div>
              <div className="mt-1"><Delta v={ex.transactions_change} /></div>
            </Card>
            <Card data-testid="kpi-customers">
              <div className="flex items-center justify-between"><div className="hsc-label">Customer</div><Users className="h-4 w-4 text-[#3A3A3C]" /></div>
              <div className="mt-2 font-display text-2xl text-white">{ex.customers || 0}</div>
              <div className="mt-1 text-xs text-[#6E6E73]">{ex.new_customers || 0} baru · {ex.returning_customers || 0} kembali</div>
            </Card>
            <Card data-testid="kpi-bookings">
              <div className="flex items-center justify-between"><div className="hsc-label">Booking</div><CalendarCheck className="h-4 w-4 text-[#3A3A3C]" /></div>
              <div className="mt-2 font-display text-2xl text-white">{ex.bookings || 0}</div>
              <div className="mt-1"><Delta v={ex.bookings_change} /></div>
            </Card>
            <Card data-testid="kpi-memberships">
              <div className="flex items-center justify-between"><div className="hsc-label">Membership Aktif</div><BadgeCheck className="h-4 w-4 text-[#3A3A3C]" /></div>
              <div className="mt-2 font-display text-2xl text-white">{ex.active_memberships || 0}</div>
              <div className="mt-1 text-xs text-[#6E6E73]">{ex.expiring_memberships || 0} segera berakhir</div>
            </Card>
            <Card data-testid="kpi-avg">
              <div className="hsc-label">Rata-rata / Transaksi</div>
              <div className="mt-2 font-display text-2xl text-white">{formatIDR(ex.avg_txn_value)}</div>
              <div className="mt-1 text-xs text-[#6E6E73]">Rp/customer {formatShort(ex.revenue_per_customer)}</div>
            </Card>
          </div>

          <Card>
            <div className="mb-4 hsc-label">Tren Revenue</div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={trend} margin={{ left: -18, right: 8, top: 8 }}>
                  <defs>
                    <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#D1D1D6" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#D1D1D6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="x" stroke="#5A5A5F" fontSize={11} tickLine={false} axisLine={false} />
                  <YAxis stroke="#5A5A5F" fontSize={11} tickFormatter={formatShort} tickLine={false} axisLine={false} />
                  <Tooltip contentStyle={{ background: "#0A0A0C", border: "1px solid #2C2C2E", borderRadius: 12, color: "#fff" }} formatter={(v) => formatIDR(v)} />
                  <Area type="monotone" dataKey="v" stroke="#FFFFFF" strokeWidth={2} fill="url(#rev)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </Card>

          <div className="grid gap-6 lg:grid-cols-2">
            <Card>
              <div className="mb-4 hsc-label">Revenue per Lini</div>
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={byLine} layout="vertical" margin={{ left: 20, right: 12 }}>
                    <XAxis type="number" hide />
                    <YAxis type="category" dataKey="line" stroke="#8E8E93" fontSize={12} width={90} tickLine={false} axisLine={false} />
                    <Tooltip cursor={{ fill: "#141416" }} contentStyle={{ background: "#0A0A0C", border: "1px solid #2C2C2E", borderRadius: 12, color: "#fff" }} formatter={(v) => formatIDR(v)} />
                    <Bar dataKey="amount" radius={[0, 6, 6, 0]}>
                      {byLine.map((_, i) => <Cell key={i} fill={LINE_COLORS[i % LINE_COLORS.length]} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>

            <Card>
              <div className="mb-4 hsc-label">Top Customer</div>
              <div className="space-y-3">
                {(data.top_customers || []).slice(0, 6).map((c, i) => (
                  <div key={c.member_id} className="flex items-center justify-between" data-testid="top-customer-row">
                    <div className="flex items-center gap-3">
                      <span className="w-5 text-sm text-[#5A5A5F]">{i + 1}</span>
                      <span className="text-sm text-white">{c.name}</span>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-white">{formatIDR(c.revenue)}</div>
                      <div className="text-[11px] text-[#6E6E73]">{c.transactions} transaksi</div>
                    </div>
                  </div>
                ))}
                {!(data.top_customers || []).length && <div className="py-6 text-center text-sm text-[#6E6E73]">Belum ada data.</div>}
              </div>
            </Card>
          </div>

          {!!(data.insights || []).length && (
            <Card>
              <div className="mb-3 hsc-label">Insight Otomatis</div>
              <ul className="space-y-2">
                {data.insights.map((s, i) => (
                  <li key={i} className="flex gap-2 text-sm text-[#D1D1D6]"><span className="text-[#5A5A5F]">•</span>{s}</li>
                ))}
              </ul>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
